import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState([]); // {menu_item_id, name, price, quantity, notes, image}
  const [customerName, setCustomerName] = useState("");
  const [tableNumber, setTableNumber] = useState("");
  const [kitchenNotes, setKitchenNotes] = useState("");

  const resetDetails = useCallback(() => {
    setCustomerName("");
    setTableNumber("");
    setKitchenNotes("");
  }, []);

  const add = useCallback((menuItem) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.menu_item_id === menuItem.id);
      if (existing) {
        return prev.map((i) =>
          i.menu_item_id === menuItem.id ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [
        ...prev,
        {
          menu_item_id: menuItem.id,
          name: menuItem.name,
          price: menuItem.price,
          quantity: 1,
          notes: "",
          image: menuItem.image,
          menu_type: menuItem.menu_type,
        },
      ];
    });
  }, []);

  const setQty = useCallback((id, qty) => {
    setItems((prev) =>
      qty <= 0
        ? prev.filter((i) => i.menu_item_id !== id)
        : prev.map((i) => (i.menu_item_id === id ? { ...i, quantity: qty } : i))
    );
  }, []);

  const remove = useCallback(
    (id) => setItems((prev) => prev.filter((i) => i.menu_item_id !== id)),
    []
  );
  const clear = useCallback(() => setItems([]), []);

  const setNote = useCallback((id, note) => {
    setItems((prev) =>
      prev.map((i) => (i.menu_item_id === id ? { ...i, notes: note } : i))
    );
  }, []);

  const qtyOf = useCallback(
    (id) => items.find((i) => i.menu_item_id === id)?.quantity || 0,
    [items]
  );

  const count = useMemo(() => items.reduce((sum, i) => sum + i.quantity, 0), [items]);
  const total = useMemo(
    () => items.reduce((sum, i) => sum + i.price * i.quantity, 0),
    [items]
  );

  const value = useMemo(
    () => ({
      items, add, setQty, remove, clear, setNote, qtyOf, count, total,
      customerName, setCustomerName,
      tableNumber, setTableNumber,
      kitchenNotes, setKitchenNotes,
      resetDetails,
    }),
    [items, add, setQty, remove, clear, setNote, qtyOf, count, total,
     customerName, tableNumber, kitchenNotes, resetDetails]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
